import * as dtt from "../../../data/model/data-types.js";
import { Services } from "../../../services/Services.js";
import { Component } from "../../Component.js";

function resolveValue(value, context) {
    return typeof value === 'function' ? value.call(context) : value;
}

export class DataTable extends Component {

    constructor(parent, options = {}) {

        options = Object.assign({
            columns: [],
            data: [],
            dataModel: null,
            queryManager: null,
            editorRouteName: null,
            userCanReadData: true,
            userCanUpdateData: true,
            userCanDeleteData: true,
            onApplyFilter: null,
            onSetAllItemsSelection: null,
            onSetAllItemsDetails: null,
            onToggleItemSelection: null,
            onToggleItemDetails: null,
            onOpenItem: null,
            onEditItem: null,
            onDeleteItem: null,
        }, options || {});

        super(parent, options);

        this.columns = options.columns;
        this.data = options.data;
        this.dataModel = options.dataModel;
        this.queryManager = options.queryManager;
        this.editorRouteName = options.editorRouteName;
        this.userCanReadData = options.userCanReadData;
        this.userCanUpdateData = options.userCanUpdateData;
        this.userCanDeleteData = options.userCanDeleteData;
        this.onApplyFilter = options.onApplyFilter;
        this.onSetAllItemsSelection = options.onSetAllItemsSelection;
        this.onSetAllItemsDetails = options.onSetAllItemsDetails;
        this.onToggleItemSelection = options.onToggleItemSelection;
        this.onToggleItemDetails = options.onToggleItemDetails;
        this.onOpenItem = options.onOpenItem;
        this.onEditItem = options.onEditItem;
        this.onDeleteItem = options.onDeleteItem;
    }

    // ------------------------------------------------------------------------

    getColumns() {
        return resolveValue(this.columns, this);
    }

    getQueryManager() {
        return resolveValue(this.queryManager, this);
    }

    applyFilter() {
        if (!this.onApplyFilter)
            return;
        this.onApplyFilter.call(this);
    }

    // ------------------------------------------------------------------------

    getData() {
        return resolveValue(this.data, this);
    }

    getDataModel() {
        return resolveValue(this.dataModel, this);
    }

    // ------------------------------------------------------------------------

    getEditorRouteName() {
        return resolveValue(this.editorRouteName, this);
    }

    // ------------------------------------------------------------------------

    canUserReadData() {
        return resolveValue(this.userCanReadData, this);
    }

    canUserUpdateData() {
        return resolveValue(this.userCanUpdateData, this);
    }

    canUserDeleteData() {
        return resolveValue(this.userCanDeleteData, this);
    }

    // ------------------------------------------------------------------------

    toggleItemSelection(item) {

        if (!this.onToggleItemSelection)
            return;

        this.onToggleItemSelection.call(this, item);

    }

    toggleItemDetails(item) {

        if (!this.onToggleItemDetails)
            return;

        this.onToggleItemDetails.call(this, item);

    }

    setAllItemsSelection(value) {

        if (!this.onSetAllItemsSelection)
            return;

        this.onSetAllItemsSelection.call(this, value);

    }

    setAllItemsDetails(value) {

        if (!this.onSetAllItemsDetails)
            return;

        this.onSetAllItemsDetails.call(this, value);

    }

    // ------------------------------------------------------------------------

    openItem(item) {

        if (!this.onOpenItem)
            return;

        this.onOpenItem.call(this, item);

    }

    editItem(item) {

        if (!this.onEditItem)
            return;

        this.onEditItem.call(this, item);

    }

    deleteItem(item) {

        if (!this.onDeleteItem)
            return;

        this.onDeleteItem.call(this, item);

    }

    // ------------------------------------------------------------------------

    render(vm) {

        const ct = Services.get('templates');

        return ct.table('.hover', [
            ct.thead(this.renderHeader(vm)),
            ct.tbody(this.renderContent(vm)),
        ]);

    }

    renderHeader(vm) {

        const ct = Services.get('templates');
        const columns = this.getColumns() || [];
        const queryManager = this.getQueryManager();
        const queryOrder = (queryManager && queryManager.getQueryOrder()) || [];

        return [
            ct.trow('', {}, [
                this.renderSelectionHeaderCell(),
                ...columns.map((column) => this.renderHeaderCell(column, queryOrder)),
                this.renderDetailHeaderCell(),
            ])
        ];

    }

    renderContent(vm) {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');
        const columns = this.getColumns() || [];
        const data = this.getData() || [];

        if (!data.length) {
            return [
                ct.trow([
                    ct.tcell({ colspan: columns.length + 1 }, [
                        ct.empty({ title: i18n.t('err.no_records') }),
                    ]),
                ])
            ];
        }

        return data.map((item, rowIndex) => this.renderRow(vm, item, rowIndex));

    }

    renderRow(vm, item, rowIndex) {

        const ct = Services.get('templates');
        const columns = this.getColumns() || [];

        if (item.$details) {
            return ct.trow(this.getRowRenderStyle(item), {}, [
                this.renderSelectionCell(item),
                ct.tcell(
                    this.getCellRenderStyle(item),
                    Object.assign(this.getCellRenderProps(item) || {}, { colspan: columns.length }),
                    this.renderItemDetails(item)
                ),
                this.renderDetailCell(item),
            ]);
        }

        return ct.trow(this.getRowRenderStyle(item), {}, [
            this.renderSelectionCell(item),
            ...columns.map((column) => this.renderCell(item, column)),
            this.renderDetailCell(item),
        ]);

    }

    renderItemDetails(item) {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');
        const dataModel = this.getDataModel();
        const displayableFields = dataModel?.getDisplayableFields?.() || [];
        const buttons = [];

        if (this.canUserReadData()) {
            buttons.push(ct.iconButton('open', '.circle.tonal', { onclick: () => this.openItem(item), title: i18n.t('button.open.label') }));
        }
        if (this.canUserUpdateData()) {
            buttons.push(ct.iconButton('edit', '.circle.tonal', { onclick: () => this.editItem(item), title: i18n.t('button.edit.label') }));
        }
        if (this.canUserDeleteData()) {
            buttons.push(ct.iconButton('delete', '.circle.tonal', { onclick: () => this.deleteItem(item), title: i18n.t('button.delete.label') }));
        }

        return [
            ct.panel([
                ct.header([
                    ct.nav([
                        ct.div('.max'),
                        ...buttons
                    ]),
                ]),
                ct.section([
                    ct.grid(
                        displayableFields.map((field) => {
                            const value = field.getFormattedValue ? field.getFormattedValue(item) : field.getValue(item);
                            return ct.cell(ct.w(field.displayWidth || 6), [
                                ct.field([
                                    ct.label(field.label || field.displayName || field.name || ''),
                                    ct.div('.control', [
                                        (value === null || value === undefined || value === '') ? '\u2014' : String(value),
                                    ]),
                                ]),
                            ]);
                        })
                    ),
                ]),
            ])
        ];

    }

    // ----------------------------------------------------

    getHeaderCellRenderStyle(column) {

        if (!column)
            return '.no-wrap';

        const ct = Services.get('templates');

        let style = '';

        if (column.wordWrap === false)
            style += '.text-ellipsis';

        if (column.width)
            style += ct.cellWidthFixed(column.width);

        return style;

    }

    getHeaderCellRenderProps(column) {
        return {};

    }

    // ----------------------------------------------------

    getRowRenderStyle(item) {

        if (!item)
            return '';
        if (item.$selected)
            return '.bg-gray';
        return '';

    }

    // ----------------------------------------------------

    getCellRenderStyle(item, column) {

        if (!item)
            return '.no-wrap';

        if (!column)
            return '';

        const ct = Services.get('templates');

        let style = '';

        switch (column.alignment) {
            case 'left':
                style += '.left-align';
                break;
            case 'right':
                style += '.rigth-align';
                break;
            case 'center':
                style += '.center-align';
                break;
            case 'justify':
                style += '.justify-align';
                break;
            default:
                style += (dtt.isNumericType(column.field?.type) ? '.right-align' : '.left-align');
                break;
        }

        switch (column.cursor) {
            case 'move':
                style += '.c-move';
                break;
            case 'zoom-in':
                style += '.c-zoom-in';
                break;
            case 'zoom-out':
                style += '.c-zoom-out';
                break;
            case 'not-allowed':
                style += '.c-not-allowed';
                break;
            default:
                style += '.c-hand';
                break;
        }

        if (column.wordWrap === false)
            style += '.no-wrap';

        if (column.width)
            style += ct.cellWidthFixed(column.width);

        return style;

    }

    getCellRenderProps(item, column) {

        if (!item) {
            return {
                style: 'vertical-align:top;'
            };
        }

        if (!column)
            return {};

        return {
            onclick: () => {
                if (column.onClick) {
                    column.onClick(item);
                    return;
                }
                this.openItem(item);
            }
        };

    }

    // ---------------------------------------------------- Content

    renderSelectionHeaderCell() {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');

        if (!this.onToggleItemSelection) {
            return ct.thcell(this.getHeaderCellRenderStyle(), this.getHeaderCellRenderProps(), []);
        }

        const menuItems = [
            ct.menuItem({
                onclick: () => {
                    this.setAllItemsSelection(true);
                }
            }, [
                ct.link(null, [
                    ct.icon('square-check'),
                    ct.span([i18n.t('button.items_select_all')]),
                ])
            ]),
            ct.menuItem({
                onclick: () => {
                    this.setAllItemsSelection(false);
                }
            }, [
                ct.link(null, [
                    ct.icon('square'),
                    ct.span([i18n.t('button.items_unselect_all')]),
                ]),
            ]),
        ];

        return ct.thcell(
            this.getHeaderCellRenderStyle(),
            this.getHeaderCellRenderProps(),
            [
                ct.dropdownButton(null, 'ellipsis-v', '.circle.transparent', [
                    ct.dropdownMenu('.right', menuItems)
                ]),
            ]
        );

    }

    renderDetailHeaderCell() {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');
        const el = ct.el;

        if (!this.onToggleItemDetails) {
            return ct.thcell(this.getHeaderCellRenderStyle(), this.getHeaderCellRenderProps(), []);
        }

        const menuItems = [
            ct.menuItem({ onclick: () => { this.setAllItemsDetails(true); } }, [
                ct.link(null, [
                    ct.icon('chevron-down'),
                    i18n.t('button.items_expand_all'),
                ]),
            ]),
            ct.menuItem({ onclick: () => { this.setAllItemsDetails(false); } }, [
                ct.link(null, [
                    ct.icon('chevron-up'),
                    i18n.t('button.items_collapse_all'),
                ]),
            ]),
        ];

        return ct.thcell(
            this.getHeaderCellRenderStyle(),
            this.getHeaderCellRenderProps(),
            [
                ct.dropdownButton(null, 'ellipsis-v', '.circle.transparent', [
                    ct.dropdownMenu('.left', menuItems)
                ])
            ]
        );

    }

    renderHeaderCell(column, queryOrder) {

        const ct = Services.get('templates');
        const i18n = Services.get('i18n');

        if (!column)
            return null;

        let content;

        if (!column.orderable) {
            content = [column.label];
        } else {
            let orderNode = queryOrder.find(function (item) {
                return item.ident == column.fieldName;
            });
            let icon = orderNode ? (orderNode.direction === 'asc' ? 'sort-up' : 'sort-down') : 'sort';
            let buttonCss = '.circle.transparent' + (orderNode ? '.active' : '');

            content = [
                column.label,
                '\u00A0',
                ct.iconButton(icon, buttonCss, {
                    onclick: (e) => this.getQueryManager().updateOrder(column.fieldName, e.ctrlKey),
                    title: i18n.t('module.data_browser.hint_orderable_column')
                }),
            ];
        }

        return ct.thcell(
            this.getHeaderCellRenderStyle(column),
            this.getHeaderCellRenderProps(column),
            content
        );

    }

    renderCell(item, column) {

        const ct = Services.get('templates');
        const el = ct.el;
        const app = this.getParent().getApp();

        let content = [];
        let field = column.field;

        if (field) {
            const value = field.getValue(item);

            if (value !== null && value !== undefined) {
                if (dtt.isBoolType(field.type)) {
                    content = [
                        ct.icon(value ? 'check' : 'close')
                    ];
                } else if (field.type == 'File') {
                    const url = app.getFileThumbnailUrl(value);
                    let css = '.p-relative' + ct.getCursorStyle('zoom-in');
                    let nodes = [];

                    if (column.onClick) {
                        nodes.push(
                            ct.iconButton('search', '.small.absolute', { onclick: () => column.onClick(item), style: 'right:0.25rem;bottom:0.25rem;' })
                        );
                    }

                    nodes.push(el('a' + css, { href: url }, [
                        el('img.img-responsive', { src: url }, [])
                    ]));

                    content = nodes;
                } else if (field.formatter) {
                    //content = [field.formatter.format(value, column.format || field.format)];
                    content = [field.getFormattedValue(item, column.format)];
                }
            }
        }

        return ct.tcell(
            this.getCellRenderStyle(item, column),
            this.getCellRenderProps(item, column),
            content
        );

    }

    renderSelectionCell(item, content = null) {

        const ct = Services.get('templates');

        if (!content) {
            content = [];

            if (this.onToggleItemSelection) {
                content.push(
                    ct.iconButton(item.$selected ? 'checked' : 'unchecked', '.circle.transparent', { onclick: () => this.onToggleItemSelection(item) })
                );
            }
        }

        return ct.tcell(
            this.getCellRenderStyle(),
            this.getCellRenderProps(),
            content
        );

    }

    renderDetailCell(item, content = null) {

        const ct = Services.get('templates');

        if (!content) {
            content = [];

            if (this.onToggleItemDetails) {
                content.push(
                    ct.iconButton(item.$details ? 'collapse' : 'expand', '.circle.transparent', { onclick: () => this.onToggleItemDetails(item) })
                );
            }
        }

        return ct.tcell(
            this.getCellRenderStyle(),
            this.getCellRenderProps(),
            content
        );

    }

}
